import { DtoTicket } from './DtoTicket';

/**
 * Max quantity selectable in the current cart for this ticket.
 * purchaseLimitRemaining already excludes prior purchases (server-side).
 */
export function getEffectiveMaxQuantity(ticket: DtoTicket): number {
    const inventoryLeft = ticket.numberLeft == null ? Number.MAX_SAFE_INTEGER : ticket.numberLeft;

    if (ticket.purchaseLimitRemaining == null) {
        return inventoryLeft;
    }

    return Math.max(0, Math.min(inventoryLeft, ticket.purchaseLimitRemaining));
}

export function getQuantityMaxMessage(ticket: DtoTicket): string {
    if (ticket.purchaseLimitRemaining != null && ticket.purchaseLimitReachedMessage) {
        return ticket.purchaseLimitReachedMessage;
    }

    return 'No more tickets available.';
}

const FALLBACK_PURCHASE_LIMIT_MESSAGE = 'You have reached your limit for purchasing this ticket.';

/**
 * Clears restriction fields that were applied solely for a cart purchase-limit lock,
 * so reducing cart qty unlocks the ticket again.
 */
export function clearStalePurchaseLimitLock(ticket: DtoTicket): void {
    // Only clear when we can identify the message as the purchase-limit lock we applied.
    // Do not clear arbitrary server restriction messages that happen to match the fallback text.
    if (!ticket.purchaseLimitReachedMessage
        || ticket.restrictionMessage !== ticket.purchaseLimitReachedMessage) {
        return;
    }

    ticket.restrictionMessage = null;
    if (ticket.restrictionTitle === 'Restricted') {
        ticket.restrictionTitle = null;
    }
}

/**
 * Attendee-centric: apply purchase-limit or inventory lock messaging.
 * Mutates ticket restriction fields when locking. Returns true when locked.
 */
export function applyAttendeeCentricAvailabilityLock(
    ticket: DtoTicket,
    cartCount: number,
    hasSelectedThisTicket: boolean
): boolean {
    if (hasSelectedThisTicket) {
        clearStalePurchaseLimitLock(ticket);
        return false;
    }

    // Purchase limit: do not allow more than the purchaser's remaining allowance
    if (ticket.purchaseLimitRemaining != null && cartCount >= ticket.purchaseLimitRemaining) {
        ticket.restrictionTitle = 'Restricted';
        ticket.restrictionMessage = ticket.purchaseLimitReachedMessage
            || ticket.restrictionMessage
            || FALLBACK_PURCHASE_LIMIT_MESSAGE;
        return true;
    }

    clearStalePurchaseLimitLock(ticket);

    // Inventory sold out
    if (ticket.numberLeft <= cartCount) {
        if (!ticket.restrictionMessage) {
            ticket.restrictionTitle = 'Sold Out';
            ticket.restrictionMessage = 'There are no more tickets available for purchase.';
        } else if (!ticket.restrictionTitle) {
            ticket.restrictionTitle = ticket.purchaseLimitReachedMessage ? 'Restricted' : 'Sold Out';
        }
        return true;
    }

    return false;
}
